import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtEuro, fmtEuroCents } from "@/lib/format";
import { MONTHS } from "@/components/bizstats/KpiTab";
import { Table2 } from "lucide-react";

const DIMS = [["cat1", "Κατηγορία 1η"], ["cat2", "Κατηγορία 2η"], ["cat3", "Κατηγορία 3η"], ["supplier", "Προμηθευτής"],
  ["item", "Είδος"], ["month", "Μήνας"], ["quarter", "Τρίμηνο"], ["date", "Ημερομηνία"], ["none", "— Σύνολο —"]];
const MEASURES = [["net", "Αξία (καθαρή)"], ["qty", "Ποσότητα"], ["avg_price", "Μέση τιμή μονάδας"], ["count", "Γραμμές"]];
const sel = "max-w-full sm:max-w-[260px] rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]";

const label = (dim, k) => (dim === "month" ? MONTHS[Number(k) - 1] : dim === "date" ? new Date(k).toLocaleDateString("el-GR") : k);

export default function PurchasePivot() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState("cat2");
  const [cols, setCols] = useState("month");
  const [measure, setMeasure] = useState("net");
  const [cat1, setCat1] = useState("");
  const [supplier, setSupplier] = useState("");
  const [qIn, setQIn] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ(qIn), 350);
    return () => clearTimeout(t);
  }, [qIn]);
  const params = { year, rows, cols, measure, cat1, supplier, q };
  const { data, isLoading } = useQuery({ queryKey: ["erp-pivot", params], queryFn: async () => (await api.get("/erp/pivot", { params })).data });
  const fmt = (v) => (measure === "net" ? fmtEuro(v) : measure === "avg_price" ? fmtEuroCents(v) : new Intl.NumberFormat("el-GR", { maximumFractionDigits: 2 }).format(v || 0));
  const Pick = ({ id, value, set, opts, cls = "" }) => (
    <select data-testid={id} value={value} onChange={(e) => set(e.target.value)} className={`${sel} ${cls}`}>{opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
  );
  return (
    <div className="space-y-6" data-testid="purchase-pivot-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">SoftOne · Αγορές αναλυτικά</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 flex items-center gap-3"><Table2 className="h-8 w-8 text-[#C86235]" /> Ανάλυση Αγορών (Pivot)</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Από το αρχείο «Αγορές αναλυτικά» (καθαρές αξίες). Επιλέξτε τι θα είναι γραμμές, στήλες και μέτρο.</p>
        </div>
        <Pick id="pv-year" value={year} set={(v) => setYear(Number(v))} opts={[2025, 2026, 2027].map((y) => [y, y])} />
      </div>
      <div className="flex flex-wrap gap-3 items-end">
        <label className="space-y-1"><span className="block text-[10px] uppercase text-[#6F6A60]">Γραμμές</span><Pick id="pv-rows" value={rows} set={setRows} opts={DIMS} /></label>
        <label className="space-y-1"><span className="block text-[10px] uppercase text-[#6F6A60]">Στήλες</span><Pick id="pv-cols" value={cols} set={setCols} opts={DIMS} /></label>
        <label className="space-y-1"><span className="block text-[10px] uppercase text-[#6F6A60]">Μέτρο</span><Pick id="pv-measure" value={measure} set={setMeasure} opts={MEASURES} /></label>
        <label className="space-y-1"><span className="block text-[10px] uppercase text-[#6F6A60]">Κατηγορία 1η</span>
          <Pick id="pv-cat1" value={cat1} set={setCat1} opts={[["", "Όλες"], ...(data?.options?.cat1 || []).map((c) => [c, c])]} /></label>
        <label className="space-y-1 w-full sm:w-64 min-w-0"><span className="block text-[10px] uppercase text-[#6F6A60]">Προμηθευτής</span>
          <Pick id="pv-supplier" cls="w-full" value={supplier} set={setSupplier} opts={[["", "Όλοι"], ...(data?.options?.supplier || []).map((c) => [c, c])]} /></label>
        <label className="space-y-1"><span className="block text-[10px] uppercase text-[#6F6A60]">Αναζήτηση είδους</span>
          <input data-testid="pv-search" value={qIn} onChange={(e) => setQIn(e.target.value)} placeholder="π.χ. γάλα" className={`${sel} w-48`} /></label>
      </div>
      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-auto max-h-[70vh]" data-testid="pv-table">
        {isLoading || !data ? <p className="p-5 text-sm text-[#6F6A60]">Φόρτωση…</p> : (
          <table className="w-full text-xs">
            <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60] sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">{DIMS.find((d) => d[0] === rows)?.[1]}</th>
                {data.col_keys.map((c) => <th key={c} className="px-2 py-2 text-right whitespace-nowrap">{label(cols, c)}</th>)}
                <th className="px-3 py-2 text-right">Σύνολο</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((r, i) => (
                <tr key={r.key} className="border-t border-[#E3DED4] hover:bg-[#FAF8F4]" data-testid={`pv-row-${i}`}>
                  <td className="px-3 py-1.5 font-medium text-[#45423C] max-w-[280px] truncate" title={r.key}>{label(rows, r.key)}</td>
                  {data.col_keys.map((c) => <td key={c} className="px-2 text-right tabular whitespace-nowrap">{r.cells[c] !== undefined ? fmt(r.cells[c]) : ""}</td>)}
                  <td className="px-3 text-right tabular font-semibold whitespace-nowrap">{fmt(r.total)}</td>
                </tr>
              ))}
              <tr className="bg-[#45423C] text-white font-semibold sticky bottom-0" data-testid="pv-total">
                <td className="px-3 py-2">Σύνολο</td>
                {data.col_keys.map((c) => <td key={c} className="px-2 text-right tabular whitespace-nowrap">{fmt(data.col_totals[c])}</td>)}
                <td className="px-3 text-right tabular whitespace-nowrap">{fmt(data.total)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
      {data && data.row_count > 500 && <p className="text-xs text-[#6F6A60]">Εμφανίζονται οι 500 πρώτες από {data.row_count} γραμμές.</p>}
    </div>
  );
}
